import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react';
import {
  Button,
  Text,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
} from '@fluentui/react-components';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import { trackException } from '../../lib/telemetry';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /**
   * Monotonically-increasing key passed to the wrapping `<Fragment key={resetKey}>`
   * so a Retry click forces a fresh mount of the subtree. Without this, the
   * failed children re-render from the same React tree and any stale-state
   * closure that triggered the original throw re-throws immediately.
   */
  resetKey: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Pick<ErrorBoundaryState, 'hasError' | 'error'> {
    return { hasError: true, error };
  }

  /**
   * Forward caught render errors to the App Insights telemetry sink so they
   * surface in the same observability backend as server-side exceptions.
   *
   * `getDerivedStateFromError` remains responsible for setting fallback UI
   * state — this override is side-effect-only and does not call `setState`.
   *
   * PII discipline: the React `componentStack` string is component-name based
   * and does not carry user data. `error.message` is application-controlled
   * and MAY carry user-supplied strings depending on how the failing
   * component constructed the throw. The `length` property is forwarded
   * rather than the message body itself to avoid leaking PII into telemetry;
   * the full exception is forwarded to `trackException` which honors the
   * App Insights `disablePiiTracking` posture configured by the SDK.
   */
  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    trackException(error, 'errorBoundary', {
      componentStack: errorInfo.componentStack ?? '',
      errorMessageLength: error.message?.length ?? 0,
    });
  }

  private handleRetry = () => {
    // Clear the error state AND bump `resetKey` so the keyed Fragment wrapper
    // remounts the children subtree. Increment-based reset breaks any stale
    // closure or memoized hook that would otherwise re-throw on the next
    // render.
    this.setState((prev) => ({
      hasError: false,
      error: null,
      resetKey: prev.resetKey + 1,
    }));
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <FallbackError
          error={this.state.error ?? 'An unexpected error occurred.'}
          onRetry={this.handleRetry}
        />
      );
    }

    return <Fragment key={this.state.resetKey}>{this.props.children}</Fragment>;
  }
}

interface FallbackErrorProps {
  error: Error | string;
  onRetry?: () => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    width: '100%',
  },
  icon: {
    fontSize: '48px',
    color: tokens.colorPaletteRedForeground1,
  },
  messageBar: {
    maxWidth: '600px',
    width: '100%',
  },
});

export function FallbackError({ error, onRetry }: FallbackErrorProps) {
  const styles = useStyles();
  const message = typeof error === 'string' ? error : error.message;

  return (
    <div className={styles.container}>
      <ErrorCircleRegular className={styles.icon} />
      <MessageBar intent="error" className={styles.messageBar}>
        <MessageBarBody>
          <MessageBarTitle>Something went wrong</MessageBarTitle>
          <Text>{message}</Text>
        </MessageBarBody>
        {onRetry && (
          <MessageBarActions>
            <Button appearance="transparent" onClick={onRetry}>
              Retry
            </Button>
          </MessageBarActions>
        )}
      </MessageBar>
    </div>
  );
}
